import { useQuery } from "@apollo/client/react";
import { PermanentlyHideableBanner } from "@components/common/Banner";
import Sort from "@components/common/inbox/Sort";
import Loading from "@components/common/loading/Loading";
import {
  DSCheckbox,
  DSInput,
} from "@components/design-system/molecules";
import NoteFilters from "@components/inbox/NoteFilters";
import { renderLogoImg } from "@components/layout/header/Logo";
import { MaximizedPipControls } from "@components/pip/PipSizeControls";
import {
  faFilter,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import GET_INBOX_NOTES_LISTING from "@graphql/queries/GetInboxNotesListing";
import {
  useDebouncedValue,
  useLocalStorage,
  useTeam,
  useWatchUserWsEvent,
} from "@hooks";
import { Tooltip } from "@mui/material";
import { NOTE_SEARCH_PLACEHOLDER } from "@src/constants";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import PIMSPalIcon from "../../assets/imgs/icon-pimspal.png";
import scribenoteIcon from "../../assets/imgs/Logo_icon.png";
import NapTime from "../../assets/imgs/otto-scribbles-sleep.svg";
import LoadMore from "../common/inbox/LoadMore";
import NoteItem from "../dashboard/dashboardInbox/NoteItem";

function EmptyState() {
  return (
    <div className="p-10 w-full h-full flex">
      <div className="flex flex-col w-full self-center justify-self-center space-y-5">
        <img
          className="flex self-center"
          width="250px"
          src={NapTime}
        />

        <h4 className="text-center text-sm text-gray-500 dark:text-gray-400">
          Take a paws...
        </h4>
        <h5
          className="text-center text-xs text-gray-400 dark:text-gray-500"
          data-testid="empty-inbox-message"
        >
          There are no new notes for you to catch up on for now. Go on
          and enjoy the rest of your day! <br /> (and let Otto and
          Scribbles catch some Z&apos;s)
        </h5>
      </div>
    </div>
  );
}

export default function PipNoteList() {
  const { hideHolidayTheming } = useFlags();
  const { currentTeam, refreshUserTeams } = useTeam();
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterJobType, setFilterJobType] = useLocalStorage(
    "pip-filter-job-type",
    null,
  );
  const [selectedTeamMembers, setSelectedTeamMembers] =
    useLocalStorage("pip-selected-team-members", []);

  const [showExportedNotes, setShowExportedNotes] = useLocalStorage(
    "pip-show-exported-notes",
  );

  const [selectedRange, setSelectedRange] = useLocalStorage(
    "pip-selected-date-range",
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  );

  const { control, register } = useForm({
    defaultValues: {
      searchQuery: "",
    },
  });

  const searchQuery = useWatch({ control, name: "searchQuery" });
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 600);

  const [queryOptions, setQueryOptions] = useState({
    filter: "",
    limit: 25,
    offset: 0,
    teamUuid: currentTeam?.uuid,
    sortAscending: false,
    searchQuery: "",
    hasBeenExported: showExportedNotes ? null : false,
  });

  const { data, refetch, loading, fetchMore } = useQuery(
    GET_INBOX_NOTES_LISTING,
    {
      variables: queryOptions,
      fetchPolicy: "network-only",
    },
  );
  useWatchUserWsEvent({
    onMessage: () => refetch(queryOptions),
    eventName: "note_update",
  });

  useEffect(() => {
    setQueryOptions((prevOptions) => ({
      ...prevOptions,
      teamUuid: currentTeam?.uuid,
      userUuids: selectedTeamMembers,
      hasBeenExported: showExportedNotes ? null : false,
      jobType: filterJobType?.value,
      startDate: selectedRange.startDate,
      endDate: selectedRange.endDate,
    }));
  }, [
    currentTeam?.uuid,
    selectedTeamMembers,
    showExportedNotes,
    filterJobType?.value,
    selectedRange,
  ]);

  useEffect(() => {
    setQueryOptions((prev) => ({
      ...prev,
      searchQuery: debouncedSearchQuery,
      offset: 0,
    }));
  }, [debouncedSearchQuery]);

  async function incrementOffset() {
    setLoadingMore(true);

    try {
      await fetchMore({
        variables: {
          offset: data?.inboxNotes?.notes?.length || 0,
        },
      });
    } finally {
      setLoadingMore(false);
    }
  }

  const handleSort = async () => {
    setQueryOptions((prev) => ({
      ...prev,
      sortAscending: !prev.sortAscending,
    }));
  };

  const totalNotes = data?.inboxNotes?.totalNotes;
  const notes = data?.inboxNotes?.notes;

  return (
    <>
      <div className="sticky top-0 bg-primary py-2 z-10 -mx-1">
        <div
          className="py-2 flex flex-row items-center gap-2"
          data-mx-label="pip-scribenote-logo"
        >
          <div
            className="w-[150px] cursor-pointer self-center mb-2 [@media(max-width:350px)]:hidden"
            onClick={() => window.scrollTo(0, 0)}
          >
            {renderLogoImg(hideHolidayTheming)}
          </div>
          <div
            className="cursor-pointer self-center [@media(min-width:350px)]:hidden"
            onClick={() => window.scrollTo(0, 0)}
          >
            <img src={scribenoteIcon} className="w-[40px]" />
          </div>
          <Tooltip
            arrow
            placement="bottom"
            title="Widget Mode allows you to access Scribenote features more easily side-by-side with your PIMS/medical records. Click to learn more"
          >
            <a
              className="text-xs text-nowrap bg-cyan-500 text-white hover:text-white focus:text-white hover:no-underline focus:no-underline px-2 py-1 rounded-full font-medium"
              href="https://docs.scribenote.com/en/articles/12602342-how-to-use-widget-mode"
              target="_blank"
              rel="noreferrer"
            >
              Widget Mode <FontAwesomeIcon icon={faInfoCircle} />
            </a>
          </Tooltip>
          <div className="flex flex-row flex-1 justify-end">
            <MaximizedPipControls />
          </div>
        </div>

        <PermanentlyHideableBanner
          bannerId="pip-pimspal-all-web-pims"
          dismissButtonMxLabel="pip-pimspal-banner-dismiss"
        >
          <div>
            <a
              className="flex flex-row items-center gap-4 hover:no-underline"
              href="https://chromewebstore.google.com/detail/scribenote-pimspal/ibenkaeanlddcgpbfokocbhgbpfhieoc"
              target="_blank"
              rel="noreferrer"
              data-mx-label="pip-pimspal-banner"
            >
              <img
                src={PIMSPalIcon}
                className="cover rounded-xl max-w-[40px]"
              />
              <div className="text-left flex-1">
                Does your clinic use ezyVet, Pulse, or another{" "}
                <strong>cloud-based PIMS</strong>? Try our{" "}
                <strong>PIMSPal extension</strong> for even faster
                note copying!
              </div>
            </a>
          </div>
        </PermanentlyHideableBanner>

        <div className="flex flex-col">
          <div className="flex flex-col gap-4">
            <div className="flex flex-row items-center">
              <FontAwesomeIcon
                icon={faFilter}
                className="text-secondary mr-1"
              />
              <NoteFilters
                searchQuery={debouncedSearchQuery}
                setQueryOptions={setQueryOptions}
                currentTeam={currentTeam}
                selectedTeamMembers={selectedTeamMembers}
                setSelectedTeamMembers={setSelectedTeamMembers}
                selectedJobType={filterJobType}
                setSelectedJobType={setFilterJobType}
                refreshCurrentTeam={refreshUserTeams}
                selectedRange={selectedRange}
                setSelectedRange={setSelectedRange}
              />
              <div className="flex flex-1 justify-end">
                <Sort
                  sortAscending={queryOptions.sortAscending}
                  onSort={handleSort}
                />
              </div>
            </div>
            <div className="w-full">
              <DSInput
                leadingIcon={faSearch}
                placeholder={NOTE_SEARCH_PLACEHOLDER}
                dataTestId="pip-search-input"
                {...register("searchQuery")}
              />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <DSCheckbox
                id="show-exported-notes"
                size="sm"
                checked={showExportedNotes}
                onChange={(e) =>
                  setShowExportedNotes(e.target.checked)
                }
                data-mx-label="pip-show-exported-notes"
                dataTestId="pip-show-exported-notes"
              />
              <label
                htmlFor="show-exported-notes"
                className="cursor-pointer text-xs"
              >
                Show exported notes
              </label>
            </div>
          </div>
        </div>
      </div>
      <div>
        <Loading shouldShow={loading} />

        {notes?.map((note) => {
          return (
            <div key={note.uuid} className="my-md">
              <NoteItem
                note={note}
                href={`/pip/note/${note.uuid}`}
                dashboard
              />
            </div>
          );
        })}

        {notes?.length === 0 && <EmptyState />}

        {totalNotes > notes?.length && (
          <LoadMore
            incrementOffset={incrementOffset}
            loadingMore={loadingMore}
          />
        )}
      </div>
    </>
  );
}
